/**
 * De kaart. Eén component, twee soorten tegels, en één plek waar die keuze valt.
 *
 * ## Waarom dit bestand bestaat
 *
 * Leaflet is een kaartbibliotheek en geen kaart: de beelden (tegels) komen ergens
 * anders vandaan. Dat "ergens anders" is de eigenlijke beslissing (§17.41, ADR-141).
 * OpenStreetMap draait zelf tegelservers, maar hun gebruiksbeleid verbiedt gebruik
 * door een product als dit — niet uit onwil, maar omdat vrijwilligers de rekening
 * betalen.
 *
 * Het besluit is daarom: tegels uit een eigen **Protomaps**-bestand, één `.pmtiles`
 * op onze eigen opslag. Geen sleutel, geen vendor, geen plafond op het aantal
 * kaartweergaven.
 *
 * ## Wat deze component daarvan waarmaakt
 *
 * `MAP_TILES_URL` bepaalt wat er gebeurt, en de vorm van die waarde bepaalt de modus:
 *
 *   - eindigt op `.pmtiles`  → vectortegels uit ons eigen bestand (de productiestand);
 *   - bevat `{z}/{x}/{y}`    → gewone rastertegels (ontwikkeling, of een provider);
 *   - leeg                   → geen tegels, wél een werkende kaart.
 *
 * Dat laatste is met opzet en geen noodgreep. Zolang het `.pmtiles`-bestand er niet
 * is, moet een verhuurder zijn woning nog steeds kunnen plaatsen: hij ziet dan een
 * raster met coördinaten in plaats van straten. Een kaart die helemaal niet laadt zou
 * betekenen dat de locatie onbereikbaar is, en dat is het ergere van de twee.
 */
import { useEffect, useRef } from 'react'
import type { ReactElement } from 'react'

import 'leaflet/dist/leaflet.css'

/** Waar de kaart begint als er nog niets gekozen is: Nederland, volledig in beeld. */
const DEFAULT_CENTER: [number, number] = [52.15, 5.4]
const DEFAULT_ZOOM = 7
/** Ingezoomd zodra er een punt is: straatniveau, zodat "klopt dit" te beoordelen is. */
const PICKED_ZOOM = 16

export interface MapMarker {
  readonly id: string
  readonly latitude: number
  readonly longitude: number
  readonly label: string
  /** Wat er onder het label staat; leeg blijft weg. */
  readonly sublabel?: string
  readonly href?: string
}

interface Props {
  readonly latitude: number | null
  readonly longitude: number | null
  /** Aan zetten en de kaart wordt een invoerveld: klikken kiest een punt. */
  readonly onPick?: (latitude: number, longitude: number) => void
  readonly markers?: readonly MapMarker[]
  /** Zuid, west, noord, oost — de kaart springt hiernaartoe wanneer hij verandert. */
  readonly bounds?: readonly [number, number, number, number] | null
  readonly onBoundsChange?: (south: number, west: number, north: number, east: number) => void
  readonly height?: string
  readonly ariaLabel: string
}

function tilesUrl(): string {
  const raw: unknown = import.meta.env.VITE_MAP_TILES_URL
  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * De bronvermelding. Dit is geen beleefdheid maar een licentievoorwaarde: OpenStreetMap
 * staat onder ODbL, en die eist naamsvermelding bij elke weergave. Ook een eigen
 * `.pmtiles` is uit OSM-data gemaakt.
 */
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'

export function MapView({
  latitude,
  longitude,
  onPick,
  markers = [],
  bounds = null,
  onBoundsChange,
  height = '360px',
  ariaLabel,
}: Props): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Leaflet leeft buiten React: het beheert zijn eigen DOM. De verwijzingen staan
  // daarom in refs en niet in state — state zou bij elke muisbeweging hertekenen.
  const mapRef = useRef<import('leaflet').Map | null>(null)
  const markerLayerRef = useRef<import('leaflet').LayerGroup | null>(null)
  const pickedRef = useRef<import('leaflet').Marker | null>(null)
  const onPickRef = useRef(onPick)
  const onBoundsRef = useRef(onBoundsChange)

  // De callbacks via een ref, zodat een nieuwe functie-identiteit bij elke render
  // niet de hele kaart opnieuw opbouwt. In een effect en niet tijdens het renderen:
  // een ref aanraken tijdens de render is niet toegestaan, en met StrictMode of
  // concurrent rendering is het ook aantoonbaar fout.
  useEffect(() => {
    onPickRef.current = onPick
    onBoundsRef.current = onBoundsChange
  }, [onPick, onBoundsChange])

  useEffect(() => {
    const container = containerRef.current
    if (container === null || mapRef.current !== null) {
      return
    }

    let cancelled = false

    void (async () => {
      const leaflet = await import('leaflet')
      if (cancelled || containerRef.current === null) {
        return
      }

      const map = leaflet.map(container, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        // De schuifknop van Leaflet botst met toetsenbordnavigatie op een formulier;
        // de knoppen blijven, het scrollwiel niet.
        scrollWheelZoom: true,
      })
      mapRef.current = map

      const url = tilesUrl()
      if (url.endsWith('.pmtiles')) {
        // Vectortegels uit ons eigen bestand: de productiestand.
        const [{ PMTiles, Protocol }, protomaps] = await Promise.all([
          import('pmtiles'),
          import('protomaps-leaflet'),
        ])
        const protocol = new Protocol()
        protocol.add(new PMTiles(url))
        protomaps.leafletLayer({ url, attribution: ATTRIBUTION }).addTo(map)
      } else if (url.includes('{z}')) {
        leaflet.tileLayer(url, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map)
      }
      // Geen tegels: de kaart werkt, alleen zonder straten. Zie de kop.

      markerLayerRef.current = leaflet.layerGroup().addTo(map)

      if (onPickRef.current !== undefined) {
        map.on('click', (event: import('leaflet').LeafletMouseEvent) => {
          onPickRef.current?.(event.latlng.lat, event.latlng.lng)
        })
      }

      if (onBoundsRef.current !== undefined) {
        const report = (): void => {
          const box = map.getBounds()
          onBoundsRef.current?.(box.getSouth(), box.getWest(), box.getNorth(), box.getEast())
        }
        map.on('moveend', report)
        map.on('zoomend', report)
      }

      // Leaflet meet de container bij het opzetten; staat die op dat moment nog op
      // nul (een tab die net zichtbaar wordt), dan blijft de kaart grijs.
      window.setTimeout(() => {
        map.invalidateSize()
      }, 0)
    })()

    return () => {
      cancelled = true
      mapRef.current?.remove()
      mapRef.current = null
      markerLayerRef.current = null
      pickedRef.current = null
    }
  }, [])

  // Het gekozen punt bijwerken zonder de kaart opnieuw op te bouwen.
  useEffect(() => {
    const map = mapRef.current
    if (map === null) {
      return
    }

    void (async () => {
      const leaflet = await import('leaflet')
      if (mapRef.current === null) {
        return
      }

      if (latitude === null || longitude === null) {
        pickedRef.current?.remove()
        pickedRef.current = null
        return
      }

      if (pickedRef.current === null) {
        pickedRef.current = leaflet
          .marker([latitude, longitude], { icon: pinIcon(leaflet), keyboard: false })
          .addTo(map)
        map.setView([latitude, longitude], PICKED_ZOOM)
      } else {
        pickedRef.current.setLatLng([latitude, longitude])
      }
    })()
  }, [latitude, longitude])

  // De spelden van de zoekresultaten.
  useEffect(() => {
    const layer = markerLayerRef.current
    if (layer === null) {
      return
    }

    void (async () => {
      const leaflet = await import('leaflet')
      layer.clearLayers()
      for (const marker of markers) {
        const pin = leaflet.marker([marker.latitude, marker.longitude], {
          icon: pinIcon(leaflet),
          title: marker.label,
        })
        const sub = marker.sublabel === undefined ? '' : `<br>${escapeHtml(marker.sublabel)}`
        const label = escapeHtml(marker.label)
        pin.bindPopup(
          marker.href === undefined
            ? `<strong>${label}</strong>${sub}`
            : `<a href="${escapeHtml(marker.href)}"><strong>${label}</strong></a>${sub}`,
        )
        pin.addTo(layer)
      }
    })()
  }, [markers])

  // Naar een venster springen, bijvoorbeeld na het intypen van een plaats.
  useEffect(() => {
    const map = mapRef.current
    if (map === null || bounds === null) {
      return
    }
    const [south, west, north, east] = bounds
    map.fitBounds([
      [south, west],
      [north, east],
    ])
  }, [bounds])

  return (
    <div
      ref={containerRef}
      style={{ height, width: '100%', borderRadius: 'var(--radius)', zIndex: 0 }}
      role="application"
      aria-label={ariaLabel}
    />
  )
}

/**
 * De speld, als inline SVG.
 *
 * Leaflet wijst standaard naar `marker-icon.png` naast zijn eigen CSS, en die weg
 * klopt niet meer nadat een bundler de bestanden heeft verplaatst — het klassieke
 * gebroken-icoon-probleem. Zelf tekenen is minder werk dan dat repareren, en het
 * scheelt twee netwerkverzoeken.
 */
function pinIcon(leaflet: typeof import('leaflet')): import('leaflet').DivIcon {
  return leaflet.divIcon({
    className: '',
    html:
      '<svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M13 0C5.8 0 0 5.8 0 13c0 9.7 13 21 13 21s13-11.3 13-21C26 5.8 20.2 0 13 0z" ' +
      'fill="#1f5d50"/><circle cx="13" cy="13" r="5" fill="#fff"/></svg>',
    iconSize: [26, 34],
    // Het punt van de speld staat onderaan het beeld en dat is wat de coördinaat
    // aanwijst; zonder dit anker wijst de kaart 17 pixels te hoog.
    iconAnchor: [13, 34],
    popupAnchor: [0, -30],
  })
}

/** De namen komen uit tenantdata en gaan een HTML-popup in. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
